import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface ContactFormPopupProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedPlan?: 'monthly' | 'annual';
}

export default function ContactFormPopup({ isOpen, onClose, preselectedPlan }: ContactFormPopupProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    plan: 'one-time',
    specialInstructions: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  // Close popup on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      if (preselectedPlan === 'monthly') {
        setFormData(prev => ({ ...prev, plan: 'monthly' }));
      } else if (preselectedPlan === 'annual') {
        setFormData(prev => ({ ...prev, plan: 'quarterly' }));
      }
    }
  }, [isOpen, preselectedPlan]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const validateForm = () => {
    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setSubmitStatus('Passwords do not match!');
      return false;
    }
    
    // Check password length
    if (formData.password.length < 8) {
      setSubmitStatus('Password must be at least 8 characters long!');
      return false;
    }

    // Check required fields
    if (!formData.firstName || !formData.lastName || !formData.email || 
        !formData.phone || !formData.address || !formData.city || !formData.zipCode) {
      setSubmitStatus('Please fill in all required fields!');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Use plan type directly from formData
      const planType = formData.plan;
      
      // Store pending profile data for after email confirmation
      const pendingProfile = {
        fullName: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state || '',
        zipCode: formData.zipCode,
        numberOfCans: 2,
        planType: planType,
        specialInstructions: formData.specialInstructions
      };
      localStorage.setItem('pendingProfile', JSON.stringify(pendingProfile));

      // Step 1: Create user account in Supabase Auth with proper redirect URL
      console.log('Creating user account...');
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            phone: formData.phone,
          },
          emailRedirectTo: `${window.location.origin}/account`
        }
      });

      if (authError) {
        console.error('Auth error:', authError);
        setSubmitStatus(`Account creation failed: ${authError.message}`);
        setIsSubmitting(false);
        return;
      }

      if (!authData.user) {
        setSubmitStatus('Failed to create account. Please try again.');
        setIsSubmitting(false);
        return;
      }

      console.log('User account created:', authData.user.id);

      // Check if email confirmation is required
      if (authData.user.identities && authData.user.identities.length === 0) {
        // Email confirmation required
        setSubmitStatus('Account created! Please check your email to confirm your account, then you can proceed with payment.');
        setIsSubmitting(false);
        setTimeout(() => {
          onClose();
        }, 5000);
        return;
      }

      // If no email confirmation needed, proceed with checkout
      await proceedToCheckout(authData.user.id, planType);

    } catch (error: any) {
      console.error('Submission error:', error);
      setSubmitStatus(`An error occurred: ${error.message || 'Please try again'}`);
      setIsSubmitting(false);
    }
  };

  const proceedToCheckout = async (userId: string, planType: string) => {
    try {
      // Store customer data in Supabase
      console.log('Storing customer data...');
      const { error: customerError } = await supabase
        .from('customers')
        .insert({
          user_id: userId,
          full_name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state || '',
          zip_code: formData.zipCode,
          plan: planType,
          number_of_cans: 2,
          special_preferences: formData.specialInstructions || null,
          status: 'pending',
        });

      if (customerError) {
        console.error('Customer data error:', customerError);
      } else {
        console.log('Customer data stored successfully');
      }

      // Create Stripe checkout session
      console.log('Creating checkout session...');
      const currentUrl = window.location.origin;
      const successUrl = planType === 'one-time' 
        ? `${currentUrl}/?payment=success`
        : `${currentUrl}/account?payment=success`;
      const cancelUrl = `${currentUrl}/?payment=cancelled`;

      const checkoutResponse = await supabase.functions.invoke('create-checkout-session', {
        body: {
          planType: planType,
          additionalCans: 0,
          successUrl,
          cancelUrl,
        }
      });

      if (checkoutResponse.error) {
        console.error('Checkout error:', checkoutResponse.error);
        setSubmitStatus(`Payment setup failed: ${checkoutResponse.error.message}`);
        setIsSubmitting(false);
        return;
      }

      const { url } = checkoutResponse.data;
      
      if (!url) {
        setSubmitStatus('Failed to create checkout session. Please try again.');
        setIsSubmitting(false);
        return;
      }

      console.log('Checkout session created, redirecting to:', url);
      
      // Clear pending profile since we're proceeding
      localStorage.removeItem('pendingProfile');
      
      // Redirect to Stripe checkout
      window.location.href = url;

    } catch (error: any) {
      console.error('Checkout error:', error);
      setSubmitStatus(`Payment setup failed: ${error.message || 'Please try again'}`);
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={handleBackdropClick}
      >
        {/* Popup Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-blue-600 p-6 text-center relative">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition-colors cursor-pointer"
              aria-label="Close popup"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">Start Your Service!</h2>
            <p className="text-green-100 text-lg">Create your account and schedule your first cleaning</p>
          </div>

          {/* Form Content - Scrollable */}
          <div className="max-h-[70vh] overflow-y-auto">
            <form onSubmit={handleSubmit} className="p-6 space-y-6" data-readdy-form id="service-signup-popup">
              {/* Personal Information */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-user-line text-green-600 mr-2"></i>
                  Personal Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-phone-line text-green-600 mr-2"></i>
                  Contact Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Service Address */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-map-pin-line text-green-600 mr-2"></i>
                  Service Address
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Street Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
                        placeholder="Your city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        State
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
                        placeholder="CA"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
                        placeholder="12345"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Plan */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-calendar-line text-green-600 mr-2"></i>
                  Choose Your Plan
                </h3>
                <div className="space-y-3">
                  {[
                    { value: 'one-time', label: 'One-Time Cleaning', price: '$70', desc: 'Perfect for trying our service' },
                    { value: 'quarterly', label: 'Quarterly Cleaning', price: '$35', desc: 'Every 3 months (Save 50%!)' },
                    { value: 'monthly', label: 'Monthly Cleaning', price: '$20', desc: 'Every month (Best Value!)' }
                  ].map((plan) => (
                    <label key={plan.value} className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-green-500 transition-colors">
                      <input
                        type="radio"
                        name="plan"
                        value={plan.value}
                        checked={formData.plan === plan.value}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-green-600 focus:ring-green-500 cursor-pointer"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900 text-sm">{plan.label}</span>
                          <span className="text-lg font-bold text-green-600">{plan.price}</span>
                        </div>
                        <p className="text-xs text-gray-500">{plan.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Account Security */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-lock-line text-green-600 mr-2"></i>
                  Account Security
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        minLength={8}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all pr-12 text-sm"
                        placeholder="Minimum 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 cursor-pointer"
                      >
                        <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-sm"
                      placeholder="Confirm your password"
                    />
                  </div>
                </div>
              </div>

              {/* Special Instructions */}
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-message-3-line text-green-600 mr-2"></i>
                  Special Instructions (Optional)
                </h3>
                <div className="relative">
                  <textarea
                    name="specialInstructions"
                    value={formData.specialInstructions}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all resize-none text-sm"
                    placeholder="Any special instructions for your service? (Gate codes, bin locations, etc.)"
                  />
                  <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                    {formData.specialInstructions.length}/500 characters
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-green-700 hover:to-blue-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:transform-none whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Creating Account...
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-line mr-2"></i>
                      Create Account &amp; Start Service!
                    </>
                  )}
                </button>
              </div>

              {/* Status Message */}
              {submitStatus && (
                <div className={`text-center p-4 rounded-lg ${
                  submitStatus.includes('check your email') || submitStatus.includes('created') 
                    ? 'bg-green-100 text-green-800 border border-green-300' 
                    : 'bg-red-100 text-red-800 border border-red-300'
                }`}>
                  <p className="font-medium text-sm">{submitStatus}</p>
                </div>
              )}

              {/* Terms */}
              <div className="text-center text-xs text-gray-600">
                <p>
                  By creating an account, you agree to our{' '}
                  <a href="#" className="text-green-600 hover:underline">Terms of Service</a> and{' '}
                  <a href="#" className="text-green-600 hover:underline">Privacy Policy</a>
                </p>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
